import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { ExternalLink, X } from "lucide-react";

import { getDeployment } from "@/api/tenants";
import { DeploymentTimeline } from "@/components/DeploymentTimeline";
import { DeploymentStatusBadge } from "@/components/StatusBadge";
import { ErrorBox, Spinner } from "@/components/Spinner";
import { Icon, IconButton, Notice } from "@/components/ui";
import { useShellState } from "@/hooks/useShellState";
import { POLL_INTERVAL_MS, isActiveStatus } from "@/lib/deployments";
import type { SelectedTask } from "@/components/shell/ShellProvider";

/** Detail pane on the right of the task panel — step-by-step timeline of the
 *  selected run. Reuses DeploymentTimeline, wrapped in a light sub-panel since
 *  it renders dark text. */
export function TaskDetail({ task }: { task: SelectedTask }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const { closeTask } = useShellState();

  const query = useQuery({
    queryKey: ["deployment", task.tenantId, task.runId],
    queryFn: () => getDeployment(token, task.tenantId, task.runId),
    enabled: !!token,
    refetchInterval: (q) =>
      q.state.data && isActiveStatus(q.state.data.status)
        ? POLL_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
  });

  return (
    <div className="flex h-full min-h-0 w-96 shrink-0 flex-col border-l border-line">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="min-w-0">
          <p className="truncate font-mono text-xs text-ink">
            {task.tenantId} / {query.data?.stage ?? "…"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {query.data && <DeploymentStatusBadge status={query.data.status} />}
          <IconButton
            icon={X}
            label="Close detail"
            size="sm"
            onClick={closeTask}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-3 pb-3">
        {query.isLoading && <Spinner label="Loading detail…" />}
        {query.isError && <ErrorBox error={query.error} />}
        {query.data && (
          <div className="space-y-2">
            {query.data.errorMessage && (
              <Notice tone="danger">
                <pre className="whitespace-pre-wrap break-words font-mono text-xs">
                  {query.data.errorMessage}
                </pre>
              </Notice>
            )}
            <div className="rounded border border-line bg-surface p-3">
              <DeploymentTimeline deployment={query.data} />
            </div>
            {query.data.ghaRunUrl && (
              <a
                href={query.data.ghaRunUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
              >
                Detailed log
                <Icon icon={ExternalLink} size="sm" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
